<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import {
  getMostRecentAcademicSession,
  sortAcademicSessionsDescending,
} from "@/utils/academicSessionUtils";

const STUDENT_STATUSES = [
  "active",
  "inactive",
  "graduated",
  "suspended",
  "withdrawn",
  "deferred",
  "expelled",
  "completed",
];

export default {
  name: "StudentsMgt",
  data() {
    return {
      students: [],
      academicSessions: [],
      programOfferings: [],
      selectedStudent: null,
      loading: false,
      detailsLoading: false,
      refreshing: false,
      exporting: false,
      initialized: false,
      searchQuery: "",
      searchTimer: null,
      filters: {
        status: "",
        entrySession: "",
        academicSession: "",
        program: "",
        year: "",
        semester: "",
        dateRange: { start: "", end: "" },
      },
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 1,
      },
      sortBy: "createdAt",
      sortOrder: "desc",
      statuses: STUDENT_STATUSES,
    };
  },
  computed: {
    ...mapGetters(["hasPermission"]),
    canManageStudents() {
      return this.hasPermission("students", "manage");
    },
    pageRange() {
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;
      let start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },
    paginationInfo() {
      const start = this.pagination.totalItems
        ? (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1
        : 0;
      return {
        start,
        end: Math.min(
          this.pagination.currentPage * this.pagination.itemsPerPage,
          this.pagination.totalItems
        ),
        total: this.pagination.totalItems,
      };
    },
    availableProgramOfferings() {
      return this.programOfferings
        .filter((offering) => offering.active !== false)
        .map((offering) => ({
          _id: offering._id,
          label: this.programLabel(offering),
        }));
    },
    sortedAcademicSessions() {
      return sortAcademicSessionsDescending(this.academicSessions);
    },
  },
  watch: {
    searchQuery() {
      if (!this.initialized) return;
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.fetchStudents(1), 350);
    },
    "pagination.itemsPerPage"() {
      if (this.initialized) this.fetchStudents(1);
    },
    "filters.status"() {
      if (this.initialized) this.fetchStudents(1);
    },
    "filters.entrySession"() {
      if (this.initialized) this.fetchStudents(1);
    },
    "filters.academicSession"() {
      if (this.initialized) this.fetchStudents(1);
    },
    "filters.program"() {
      if (this.initialized) this.fetchStudents(1);
    },
    "filters.year"() {
      if (this.initialized) this.fetchStudents(1);
    },
    "filters.semester"() {
      if (this.initialized) this.fetchStudents(1);
    },
    "filters.dateRange.start"() {
      if (this.initialized) this.fetchStudents(1);
    },
    "filters.dateRange.end"() {
      if (this.initialized) this.fetchStudents(1);
    },
  },
  async mounted() {
    await this.fetchInitialData();
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
  },
  methods: {
    requestParams(page = 1, limit = this.pagination.itemsPerPage) {
      return {
        page,
        limit,
        search: this.searchQuery.trim() || undefined,
        status: this.filters.status || undefined,
        entrySessionId: this.filters.entrySession || undefined,
        academicSessionId: this.filters.academicSession || undefined,
        programOfferingId: this.filters.program || undefined,
        year: this.filters.year || undefined,
        semester: this.filters.semester || undefined,
        startDate: this.filters.dateRange.start || undefined,
        endDate: this.filters.dateRange.end || undefined,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      };
    },
    async fetchInitialData() {
      this.loading = true;
      try {
        const [sessions, offerings] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions`),
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-offerings`),
        ]);
        this.academicSessions = Array.isArray(sessions.data)
          ? sessions.data
          : [];
        this.programOfferings = Array.isArray(offerings.data)
          ? offerings.data
          : [];
        const mostRecentSession = getMostRecentAcademicSession(
          this.academicSessions
        );
        this.filters.academicSession = mostRecentSession?._id || "";
        this.initialized = true;
        await this.fetchStudents(1);
      } catch (error) {
        this.showError(error, "Failed to load Students Management");
      } finally {
        this.loading = false;
      }
    },
    async fetchStudents(page = this.pagination.currentPage) {
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/students`,
          { params: this.requestParams(page) }
        );
        this.students = response.data.data.students || [];
        const pagination = response.data.data.pagination || {};
        this.pagination.currentPage = pagination.page || page;
        this.pagination.totalItems = pagination.total || 0;
        this.pagination.totalPages = pagination.pages || 1;
      } catch (error) {
        this.showError(error, "Failed to fetch students");
      } finally {
        this.loading = false;
      }
    },
    async refreshStudents() {
      this.refreshing = true;
      await this.fetchStudents(this.pagination.currentPage);
      this.refreshing = false;
    },
    clearFilters() {
      this.initialized = false;
      this.searchQuery = "";
      this.filters = {
        status: "",
        entrySession: "",
        academicSession:
          getMostRecentAcademicSession(this.academicSessions)?._id || "",
        program: "",
        year: "",
        semester: "",
        dateRange: { start: "", end: "" },
      };
      this.$nextTick(() => {
        this.initialized = true;
        this.fetchStudents(1);
      });
    },
    fullName(student) {
      const user = student.userId || {};
      return (
        [user.lastName, user.firstName, user.otherName]
          .filter(Boolean)
          .join(" ") || "N/A"
      );
    },
    programLabel(offering) {
      if (!offering) return "N/A";
      return (
        [
          offering.structure?.type?.type,
          offering.structure?.mode?.description ||
            offering.structure?.mode?.mode,
          offering.program?.name,
        ]
          .filter(Boolean)
          .join(" ") || "N/A"
      );
    },
    statusLabel(status) {
      return String(status || "N/A")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    statusClass(status) {
      return (
        {
          active: "text-bg-success",
          inactive: "text-bg-secondary",
          graduated: "text-bg-primary",
          suspended: "text-bg-warning",
          withdrawn: "text-bg-dark",
          deferred: "text-bg-info",
          expelled: "text-bg-danger",
          completed: "text-bg-primary",
        }[status] || "text-bg-secondary"
      );
    },
    formatDate(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? "N/A"
        : date.toLocaleDateString("en-GB");
    },
    async viewStudent(student) {
      this.detailsLoading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/students/${student._id}`
        );
        this.selectedStudent = response.data.data;
      } catch (error) {
        this.showError(error, "Failed to load student details");
      } finally {
        this.detailsLoading = false;
      }
    },
    async changeStatus(student) {
      const result = await Swal.fire({
        title: "Change Student Status",
        input: "select",
        inputOptions: Object.fromEntries(
          this.statuses.map((status) => [status, this.statusLabel(status)])
        ),
        inputValue: student.status,
        showCancelButton: true,
        confirmButtonText: "Continue",
      });
      if (!result.isConfirmed || result.value === student.status) return;
      const reason = await Swal.fire({
        title: "Reason for status change",
        input: "textarea",
        inputValidator: (value) =>
          !value?.trim() ? "A reason is required" : undefined,
        showCancelButton: true,
        confirmButtonText: "Update Status",
      });
      if (!reason.isConfirmed) return;
      try {
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/students/${student._id}/status`,
          { status: result.value, reason: reason.value.trim() }
        );
        this.selectedStudent = null;
        await this.fetchStudents(this.pagination.currentPage);
        await Swal.fire("Success", "Student status updated", "success");
      } catch (error) {
        this.showError(error, "Failed to update student status");
      }
    },
    paymentName(payment) {
      return (
        payment.payment?.payment?.description ||
        payment.payment?.payment?.name ||
        "Payment"
      );
    },
    money(value) {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
      }).format(Number(value || 0));
    },
    async fetchAllFilteredStudents() {
      const first = await axios.get(
        `${process.env.VUE_APP_API_BASE_URL}/students`,
        {
          params: this.requestParams(1, 200),
        }
      );
      const students = [...(first.data.data.students || [])];
      const pages = first.data.data.pagination?.pages || 1;
      for (let page = 2; page <= pages; page += 1) {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/students`,
          { params: this.requestParams(page, 200) }
        );
        students.push(...(response.data.data.students || []));
      }
      return students;
    },
    exportRow(student) {
      return [
        student.matriculationNumber,
        this.fullName(student),
        student.userId?.email || "",
        student.userId?.phone || "",
        student.applicationId?.applicationNumber || "",
        this.programLabel(student.initialProgramOfferingId),
        student.entrySessionId?.sessionYear || "",
        student.currentEnrollmentId?.academicSessionId?.sessionYear || "",
        student.currentEnrollmentId?.year || "",
        student.currentEnrollmentId?.semester || "",
        this.statusLabel(student.status),
        this.formatDate(student.enrolledAt),
      ];
    },
    async exportStudents(format) {
      this.exporting = true;
      try {
        const students = await this.fetchAllFilteredStudents();
        if (!students.length) {
          await Swal.fire(
            "No Data",
            "No students match the current filters",
            "warning"
          );
          return;
        }
        const headers = [
          "Matriculation No.",
          "Name",
          "Email",
          "Phone",
          "Application No.",
          "Programme",
          "Entry Session",
          "Current Session",
          "Year",
          "Semester",
          "Status",
          "Enrolled",
        ];
        if (format === "csv") {
          const cell = (value) =>
            `"${String(value ?? "").replace(/"/g, '""')}"`;
          const content = [
            headers,
            ...students.map((student) => this.exportRow(student)),
          ]
            .map((row) => row.map(cell).join(","))
            .join("\n");
          const url = URL.createObjectURL(
            new Blob([content], { type: "text/csv" })
          );
          const link = document.createElement("a");
          link.href = url;
          link.download = `students_${new Date()
            .toISOString()
            .slice(0, 10)}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        } else {
          const jsPDF = (await import("jspdf")).default;
          await import("jspdf-autotable");
          const document = new jsPDF({ orientation: "landscape" });
          document.text("Student Management Report", 14, 16);
          document.autoTable({
            head: [headers],
            body: students.map((student) => this.exportRow(student)),
            startY: 22,
            styles: { fontSize: 6 },
          });
          document.save(
            `students_${new Date().toISOString().slice(0, 10)}.pdf`
          );
        }
        await Swal.fire(
          "Success",
          `Exported ${students.length} students`,
          "success"
        );
      } catch (error) {
        this.showError(error, "Failed to export students");
      } finally {
        this.exporting = false;
      }
    },
    showError(error, fallback) {
      Swal.fire("Error", error.response?.data?.message || fallback, "error");
    },
  },
};
</script>

<template>
  <div class="container py-4 text-start">
    <div
      class="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4"
    >
      <div>
        <h4 class="fw-bold mb-1">Students Management</h4>
        <p class="text-muted mb-0">
          Manage enrolled-student records and academic standing.
        </p>
      </div>
      <div class="d-flex gap-2">
        <button
          class="btn btn-sm btn-outline-primary"
          :disabled="refreshing"
          @click="refreshStudents"
        >
          <i
            class="bi bi-arrow-clockwise me-1"
            :class="{ spin: refreshing }"
          ></i
          >Refresh
        </button>
        <div class="dropdown">
          <button
            class="btn btn-sm btn-dark dropdown-toggle"
            data-bs-toggle="dropdown"
            :disabled="exporting"
          >
            <i class="bi bi-download me-1"></i>Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <button class="dropdown-item" @click="exportStudents('csv')">
                Export CSV
              </button>
            </li>
            <li>
              <button class="dropdown-item" @click="exportStudents('pdf')">
                Export PDF
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="row g-3 mb-4">
      <div class="col-md-7">
        <div class="input-group">
          <span class="input-group-text bg-white"
            ><i class="bi bi-search"></i></span
          ><input
            v-model="searchQuery"
            class="form-control border-start-0"
            placeholder="Search by name, email, phone, application or matriculation number"
          />
        </div>
      </div>
      <div class="col-md-5 d-flex justify-content-md-end">
        <div class="dropdown">
          <button
            class="btn btn-sm btn-outline-secondary dropdown-toggle"
            data-bs-toggle="dropdown"
          >
            <i class="bi bi-funnel me-1"></i>Filters
          </button>
          <div
            class="dropdown-menu dropdown-menu-end p-3 filters-menu"
            @click.stop
          >
            <div class="d-flex justify-content-between mb-3">
              <strong>Advanced Filters</strong
              ><button class="btn btn-link btn-sm p-0" @click="clearFilters">
                Clear all
              </button>
            </div>
            <label class="small text-muted">Enrolment Date</label>
            <div class="row g-2 mb-3">
              <div class="col-6">
                <input
                  v-model="filters.dateRange.start"
                  type="date"
                  class="form-control form-control-sm"
                />
              </div>
              <div class="col-6">
                <input
                  v-model="filters.dateRange.end"
                  type="date"
                  class="form-control form-control-sm"
                />
              </div>
            </div>
            <label class="small text-muted">Programme</label
            ><select
              v-model="filters.program"
              class="form-select form-select-sm mb-3"
            >
              <option value="">All programmes</option>
              <option
                v-for="offering in availableProgramOfferings"
                :key="offering._id"
                :value="offering._id"
              >
                {{ offering.label }}
              </option>
            </select>
            <div class="row g-2 mb-3">
              <div class="col-6">
                <label class="small text-muted">Year</label
                ><select
                  v-model="filters.year"
                  class="form-select form-select-sm"
                >
                  <option value="">All</option>
                  <option
                    v-for="year in [1, 2, 3, 4, 5]"
                    :key="year"
                    :value="year"
                  >
                    Year {{ year }}
                  </option>
                </select>
              </div>
              <div class="col-6">
                <label class="small text-muted">Semester</label
                ><select
                  v-model="filters.semester"
                  class="form-select form-select-sm"
                >
                  <option value="">All</option>
                  <option :value="1">First</option>
                  <option :value="2">Second</option>
                </select>
              </div>
            </div>
            <label class="small text-muted">Status</label
            ><select
              v-model="filters.status"
              class="form-select form-select-sm"
            >
              <option value="">All statuses</option>
              <option v-for="status in statuses" :key="status" :value="status">
                {{ statusLabel(status) }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="card border-0 shadow-sm">
      <div
        class="card-header bg-white d-flex justify-content-start align-items-center"
      >
        <span class="small text-muted me-2">Academic session:</span
        ><select
          v-model="filters.academicSession"
          class="form-select form-select-sm session-select"
        >
          <option value="">All sessions</option>
          <option
            v-for="session in sortedAcademicSessions"
            :key="session._id"
            :value="session._id"
          >
            {{ session.sessionYear }}
          </option>
        </select>
      </div>
      <div class="card-body p-0">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">Matric No.</th>
              <th>Student</th>
              <th>Contact</th>
              <th>Programme</th>
              <th>Session</th>
              <th>Year / Semester</th>
              <th>Status</th>
              <th class="text-end pe-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="text-center py-5">
                <div class="spinner-border"></div>
              </td>
            </tr>
            <tr v-else-if="!students.length">
              <td colspan="8" class="text-center py-5 text-muted">
                No enrolled students matched the current filters.
              </td>
            </tr>
            <tr v-for="student in students" v-else :key="student._id">
              <td class="ps-4">{{ student.matriculationNumber }}</td>
              <td class="text-capitalize">
                {{ fullName(student)
                }}<small class="d-block text-muted">{{
                  student.applicationId?.applicationNumber
                }}</small>
              </td>
              <td>
                {{ student.userId?.email
                }}<small class="d-block text-muted">{{
                  student.userId?.phone
                }}</small>
              </td>
              <td>{{ programLabel(student.initialProgramOfferingId) }}</td>
              <td>
                {{
                  student.currentEnrollmentId?.academicSessionId?.sessionYear
                }}
              </td>
              <td class="text-center">
                Year {{ student.currentEnrollmentId?.year }} Semester
                {{ student.currentEnrollmentId?.semester }}
              </td>
              <td>
                <span class="badge" :class="statusClass(student.status)">
                  {{ statusLabel(student.status) }}
                </span>
              </td>
              <td class="text-center pe-4">
                <div class="dropdown">
                  <a
                    class="dropdown-toggle no-caret text-decoration-none text-dark fs-5"
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i class="bi bi-three-dots-vertical"></i>
                  </a>

                  <ul class="dropdown-menu">
                    <li>
                      <a
                        class="dropdown-item"
                        href="#"
                        @click.prevent="viewStudent(student)"
                      >
                        <i class="bi bi-eye"></i> View
                      </a>
                    </li>
                    <li v-if="canManageStudents">
                      <a
                        class="dropdown-item"
                        href="#"
                        @click.prevent="changeStatus(student)"
                      >
                        <i class="bi bi-pencil"></i> Edit Status
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-3"
      >
        <div class="d-flex align-items-center gap-2">
          <small>Per page</small
          ><select
            v-model.number="pagination.itemsPerPage"
            class="form-select form-select-sm per-page"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <ul class="pagination pagination-sm mb-0">
          <li
            class="page-item"
            :class="{ disabled: pagination.currentPage === 1 }"
          >
            <button class="page-link" @click="fetchStudents(1)">&laquo;</button>
          </li>
          <li
            v-for="page in pageRange"
            :key="page"
            class="page-item"
            :class="{ active: page === pagination.currentPage }"
          >
            <button class="page-link" @click="fetchStudents(page)">
              {{ page }}
            </button>
          </li>
          <li
            class="page-item"
            :class="{
              disabled: pagination.currentPage === pagination.totalPages,
            }"
          >
            <button
              class="page-link"
              @click="fetchStudents(pagination.totalPages)"
            >
              &raquo;
            </button>
          </li>
        </ul>
        <small
          >Showing {{ paginationInfo.start }}-{{ paginationInfo.end }} of
          {{ paginationInfo.total }} students</small
        >
      </div>
    </div>
    <div v-if="detailsLoading" class="modal-bg">
      <div class="spinner-border text-light"></div>
    </div>
    <div
      v-if="selectedStudent"
      class="modal-bg"
      @click.self="selectedStudent = null"
    >
      <div class="details-card bg-white rounded shadow">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <div>
            <h5 class="mb-0 text-capitalize">
              {{ fullName(selectedStudent) }}
            </h5>
            <small class="text-muted">{{
              selectedStudent.matriculationNumber
            }}</small>
          </div>
          <button class="btn-close" @click="selectedStudent = null"></button>
        </div>
        <div class="p-4 details-scroll">
          <div class="row g-3">
            <div class="col-md-6">
              <strong>Application:</strong>
              {{ selectedStudent.applicationId?.applicationNumber }}
            </div>
            <div class="col-md-6">
              <strong>Status:</strong>
              <span
                class="badge"
                :class="statusClass(selectedStudent.status)"
                >{{ statusLabel(selectedStudent.status) }}</span
              >
            </div>
            <div class="col-md-6">
              <strong>Email:</strong> {{ selectedStudent.userId?.email }}
            </div>
            <div class="col-md-6">
              <strong>Phone:</strong> {{ selectedStudent.userId?.phone }}
            </div>
            <div class="col-12">
              <strong>Programme:</strong>
              {{ programLabel(selectedStudent.initialProgramOfferingId) }}
            </div>
            <div class="col-md-6">
              <strong>Entry Session:</strong>
              {{ selectedStudent.entrySessionId?.sessionYear }}
            </div>
            <div class="col-md-6">
              <strong>Admission Year:</strong>
              {{ selectedStudent.admissionYear }}
            </div>
            <div class="col-md-6">
              <strong>Current Session:</strong>
              {{
                selectedStudent.currentEnrollmentId?.academicSessionId
                  ?.sessionYear
              }}
            </div>
            <div class="col-md-6">
              <strong>Year / Semester:</strong>
              {{ selectedStudent.currentEnrollmentId?.year }} /
              {{ selectedStudent.currentEnrollmentId?.semester }}
            </div>
            <div class="col-md-6">
              <strong>Enrolled:</strong>
              {{ formatDate(selectedStudent.enrolledAt) }}
            </div>
          </div>
          <hr />
          <h6 class="fw-bold">Payment History</h6>
          <p v-if="!selectedStudent.payments?.length" class="text-muted">
            No payment records found.
          </p>
          <div
            v-for="payment in selectedStudent.payments"
            :key="payment._id"
            class="d-flex justify-content-between border-bottom py-2"
          >
            <span
              >{{ paymentName(payment)
              }}<small class="d-block text-muted">{{
                payment.paymentRef
              }}</small></span
            ><span class="text-end"
              ><strong>{{ money(payment.amount) }}</strong
              ><small class="d-block">{{
                statusLabel(payment.status)
              }}</small></span
            >
          </div>
          <div v-if="canManageStudents" class="mt-4">
            <button
              class="btn btn-outline-primary"
              @click="changeStatus(selectedStudent)"
            >
              <i class="bi bi-pencil me-1"></i>Change status
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filters-menu {
  min-width: 340px;
}
.session-select {
  max-width: 220px;
}
.per-page {
  width: 82px;
}
.modal-bg {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.details-card {
  width: min(850px, 100%);
  max-height: 92vh;
  overflow: hidden;
}
.details-scroll {
  max-height: 82vh;
  overflow: auto;
}
.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
